/**
 * Delivery Estimate Component
 * 
 * Custom element that displays delivery estimate for a product variant.
 * Uses the Section Rendering API to fetch delivery data when variant changes.
 */

if (!customElements.get('delivery-estimate')) {
  customElements.define(
    'delivery-estimate',
    class DeliveryEstimate extends HTMLElement {
      constructor() {
        super();

        this.errorHtml = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);
        this.onClickRefresh = this.onClickRefresh.bind(this);
        this.onBundleStateChanged = this.onBundleStateChanged.bind(this);
        this.bundleAvailable = null;

        // Only fetch if we have the required attributes
        if (this.dataset.variantId) {
          this.fetchDeliveryEstimate(this.dataset.variantId);
        }
      }

      connectedCallback() {
        document.addEventListener('bundle:stateChanged', this.onBundleStateChanged);
      }

      disconnectedCallback() {
        document.removeEventListener('bundle:stateChanged', this.onBundleStateChanged);
      }

      get rootUrl() {
        let url = this.dataset.rootUrl || '/';
        if (!url.endsWith('/')) {
          url = url + '/';
        }
        return url;
      }

      /**
       * Fetch delivery estimate for a specific variant
       * @param {string|number} variantId - The variant ID to fetch delivery estimate for
       */
      fetchDeliveryEstimate(variantId) {
        if (!variantId) return;

        const variantSectionUrl = `${this.rootUrl}variants/${variantId}/?section_id=delivery-estimate`;

        fetch(variantSectionUrl)
          .then((response) => {
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            return response.text();
          })
          .then((text) => {
            const sectionInnerHTML = new DOMParser().parseFromString(text, 'text/html').querySelector('.shopify-section');
            this.renderPreview(sectionInnerHTML);
          })
          .catch((e) => {
            console.error('Delivery estimate fetch error:', e);
            const button = this.querySelector('button');
            if (button) button.removeEventListener('click', this.onClickRefresh);
            this.renderError();
          });
      }

      onClickRefresh(evt) {
        evt.preventDefault();
        this.fetchDeliveryEstimate(this.dataset.variantId);
      }

      renderError() {
        if (!this.errorHtml) return;

        this.innerHTML = '';
        this.appendChild(this.errorHtml.cloneNode(true));

        const button = this.querySelector('button');
        if (button) {
          button.addEventListener('click', this.onClickRefresh);
        }
      }

      renderPreview(sectionInnerHTML) {
        const preview = sectionInnerHTML?.querySelector('delivery-estimate-preview');

        if (!preview || preview.innerHTML.trim() === '') {
          this.innerHTML = '';
          this.removeAttribute('available');
          return;
        }

        this.innerHTML = preview.outerHTML;
        this.setAttribute('available', '');

        if (this.bundleAvailable !== null) {
          this.applyBundleState(this.bundleAvailable);
        }
      }

      onBundleStateChanged(event) {
        const { isAvailable } = event.detail;
        this.bundleAvailable = isAvailable;
        this.applyBundleState(isAvailable);
      }

      /**
       * Toggle delivery content visibility based on bundle component availability.
       * When components are unavailable, hides delivery info and shows "only in stores" message.
       */
      applyBundleState(isAvailable) {
        const preview = this.querySelector('delivery-estimate-preview');
        if (!preview) return;

        const storeOnlyEl = preview.querySelector('.store_product_show_title');
        const deliveryContent = preview.querySelector('.delivery-estimate__content');
        const orderProductEl = preview.querySelector('.delivery-estimate__order-product');

        if (!isAvailable) {
          if (deliveryContent) deliveryContent.style.display = 'none';
          if (orderProductEl) orderProductEl.style.display = 'none';

          if (storeOnlyEl) {
            storeOnlyEl.style.display = '';
          } else {
            const storeOnlyText = this.dataset.onlyInStoresText || '';
            if (storeOnlyText) {
              const p = document.createElement('p');
              p.className = 'delivery-estimate__store-only store_product_show_title';
              p.dataset.bundleInjected = 'true';
              p.textContent = storeOnlyText;
              preview.prepend(p);
            }
          }
        } else {
          const injectedEl = preview.querySelector('[data-bundle-injected]');
          if (injectedEl) injectedEl.remove();
          if (storeOnlyEl && !storeOnlyEl.dataset.bundleInjected) {
            // Server-rendered store-only message — leave it as-is
          }
          if (deliveryContent) deliveryContent.style.display = '';
          if (orderProductEl) orderProductEl.style.display = '';
        }
      }
    }
  );
}
